import React, { useEffect, useLayoutEffect, useState } from 'react'
import { useQuery } from 'react-query'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { connect } from 'react-redux'
import arraySort from 'array-sort'
import styled from 'styled-components'
import ReactTooltip from 'react-tooltip'
import { push } from 'react-router-redux'
import moment from 'moment'
import colors from '../../../configuration/colors'
import withApiData from '../../HOC/withApiData'
import Button from '../../mini/Buttons/Button'
import { hideModal, showModal } from '../../../actions/modal'
import { convertToRomanNumerals } from '../../../utils/convertToRomanNumerals'
import EditWhiteSvg from '../../../static/images/mycourses/EditWhite.svg'
import {
  fetchMentorFiles,
  fetchResources,
  fetchLinkedSkills,
  fetchSingleActivity
} from './API'
import Caption1 from '../../typography/captions/Caption1'
import Caption6 from '../../typography/captions/Caption6'
import H3 from '../../typography/headers/H3'
import EditActivityDetails from './EditActivityDetails'
import ReadOnlyActivityData from './ReadOnlyActivityData'
import {
  fetchActivityCourse,
  fetchActivityCourseJourney
} from '../Journeys/API'
import { fetchJourneyLevel } from '../Levels/API'
import { cleanHexCode } from '../util'
import BackButton from '../../mini/MyCourses/BackButton'

const PageContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 820px;
  margin: auto;
`

const FlexContainer = styled.div`
  display: flex;
  align-content: center;
  align-items: center;
  justify-content: space-between;
`

const HeaderContainer = styled(FlexContainer)`
  width: 100%;
  margin-top: 55px;
  margin-bottom: 18px;
`

const ActivityContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 24px;
  border-radius: 4px;
  ${props => (!props.isEditing && `background-color: ${colors.white}`)};
`

const ImageContainer = styled.div`
  height: 52px;
  width: 52px;
  background-color: #D8D8D8;
  box-shadow: 0 0 0 1.3px #979797;
  margin: 1.3px;
  margin-right: 15px;
  border-radius: 6.5px;
  cursor: ${props => (props.isEditable ? 'pointer' : 'default')};
`

const EditIcon = styled.div`
  background-image: url(${EditWhiteSvg});
  height: 23px;
  width: 23px;
  background-repeat: no-repeat;
  margin-right: 11px;
  margin-left: 18px;
  margin-top: 16px;
`

const HoveredBackground = styled.div`
  background-color: #000;
  opacity: 0.4;
  width: 52px;
  height: 52px;
  position: absolute;
  border-radius: 6.5px;
`

const Title = styled.h2`
  color: ${colors.victoria};
  font-family: Montserrat, sans-serif;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 27pxpx;
  margin: 0 14px 0 0;
`

export const ActivityDetails = ({
  apiData,
  courseId,
  journeyId,
  levelId,
  activityQuestId
}) => {
  const dispatch = useDispatch()
  const gradeLevels = useSelector(state => state.gradelevels.items)

  const [isHovered, setIsHovered] = useState(false)

  const navigationPathname = useSelector(state => state.navigation.currentPath)
  const isEdit = navigationPathname.split('/')[9] === 'edit'

  const { isLoading: isActivityCourseLoading, data: activityCourse } = useQuery(
    ['activity-course-' + courseId],
    () => fetchActivityCourse(apiData.orgId, apiData.token, courseId),
    {
      refetchOnWindowFocus: false
    }
  )

  const { isLoading: isJourneyLoading, data: journey } = useQuery(
    ['activity-course-journey-' + journeyId],
    () =>
      fetchActivityCourseJourney(
        apiData.orgId,
        apiData.token,
        courseId,
        journeyId
      ),
    {
      refetchOnWindowFocus: false
    }
  )

  const [isEditLink, setIsEditLink] = useState(isEdit)
  const [isEditingActivity, setIsEditingActivity] = useState(false)

  const { isLoading: isActivityLoading, data: activity } = useQuery(
    ['activity-' + activityQuestId],
    () => fetchSingleActivity(apiData, levelId, activityQuestId),
    {
      refetchOnWindowFocus: false
    }
  )

  const { isLoading: isMentorFilesLoading, data: mentorFiles } = useQuery(
    ['mentorFiles-' + activityQuestId],
    () => fetchMentorFiles(apiData, levelId, activityQuestId),
    {
      refetchOnWindowFocus: false
    }
  )

  const { isLoading: isResourcesLoading, data: resources } = useQuery(
    ['resources-' + activityQuestId],
    () => fetchResources(apiData, levelId, activityQuestId),
    {
      refetchOnWindowFocus: false
    }
  )

  const { isLoading: isLinkedSkillsLoading, data: linkedSkills } = useQuery(
    ['linked-skills-' + activityQuestId],
    () => fetchLinkedSkills(apiData, levelId, activityQuestId),
    {
      refetchOnWindowFocus: false
    }
  )

  const { isLoading: isLevelDataLoading, data: levelData } = useQuery(
    ['journey-level-' + levelId],
    () => fetchJourneyLevel(apiData, courseId, journeyId, levelId),
    {
      refetchOnWindowFocus: false
    }
  )

  const handleCloseEditMode = () => {
    setIsEditLink(false)
    setIsEditingActivity(false)
  }

  const onReturnToDraft = () => {
    setIsEditingActivity(true)
  }

  const handleEditClick = () => {
    if (activity.in_draft) {
      setIsEditingActivity(true)
    } else {
      dispatch(
        showModal('EDIT_PUBLISHED_ACTIVITY_MODAL', {
          levelId,
          activityQuestId,
          onReturnToDraft
        })
      )
    }
  }

  useEffect(() => {
    return () => {
      dispatch(hideModal())
    }
  }, [])

  const isStringValueSet = value => !!value && value.trim().length > 0

  const isActivityPublishable =
    !isActivityLoading &&
    activity.in_draft &&
    isStringValueSet(activity.icon_image_url) &&
    isStringValueSet(activity.name) &&
    isStringValueSet(activity.driving_question) &&
    isStringValueSet(activity.goals) &&
    isStringValueSet(activity.description) &&
    isStringValueSet(activity.goals) &&
    (activity.checkpoint === true && activity.work_product === true
      ? !!activity.artifact_description && !!activity.artifact_type_id
      : true)

  const gradeLevel =
    !!activityCourse &&
    gradeLevels.find(gl => gl.id === activityCourse.grade_level_id)
  
  const isEditing = isEditLink || isEditingActivity

  useLayoutEffect(() => {
    if (isEditing) {
      document.querySelector('#side-nav').classList.add('hidden')

      return () => {
        const sideNav = document.querySelector('#side-nav')
        if (sideNav) {
          sideNav.classList.remove('hidden')
        }
      }
    } else {
      window.scrollTo(0, 0)
    }
  }, [isEditing])

  return (
    <div>
      {isActivityLoading ||
      isJourneyLoading ||
      isActivityCourseLoading ||
      isLevelDataLoading ||
      isMentorFilesLoading ||
      isResourcesLoading ? (
        <div className="loading" />
      ) : (
        <React.Fragment>
          {!isEditing && (
            <BackButton
              backToWhere="Journey"
              onClick={() =>
                dispatch(push(`/mycourses/${courseId}/journey/${journeyId}`))
              }
            />
          )}
          <PageContainer>
            <HeaderContainer>
              <FlexContainer>
                <ImageContainer
                  isEditable={activity.in_draft}
                  onClick={
                    activity.in_draft
                      ? () =>
                          dispatch(
                            showModal('ADD_OR_REMOVE_ACTIVITY_ICON_MODAL', {
                              apiData,
                              levelId,
                              activityQuestId,
                              activity
                            })
                          )
                      : null
                  }
                >
                  {activity.icon_image_url === null ? (
                    <EditIcon />
                  ) : (
                    <div
                      style={{
                        width: '52px',
                        height: '52px',
                        position: 'relative'
                      }}
                      onMouseEnter={() => setIsHovered(true)}
                      onMouseLeave={() => setIsHovered(false)}
                    >
                      {activity.in_draft && isHovered && (
                        <div>
                          <HoveredBackground />
                          <div
                            style={{
                              position: 'absolute',
                              marginLeft: '15px',
                              marginTop: '16px'
                            }}
                          >
                            <svg
                              width="25"
                              height="25"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M11.829 3.6l2.7 2.7L4.114 16.714 0 18l1.414-3.986L11.83 3.6zM17.42.579c.772.771.772 1.928 0 2.571l-1.414 1.414-2.571-2.571L14.85.579c.771-.772 1.929-.772 2.571 0z"
                                fill="#ffffff"
                                fill-rule="nonzero"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                      <img
                        src={activity.icon_image_url}
                        style={{
                          width: '52px',
                          height: '52px',
                          borderRadius: '6.5px'
                        }}
                      />
                    </div>
                  )}
                </ImageContainer>
                <div>
                  <Caption6
                    style={{ color: cleanHexCode(activityCourse.color) }}
                  >
                    <span style={{ marginRight: '10px' }}>
                      {activityCourse.abbreviation +
                        ' / ' +
                        gradeLevel.abbreviation +
                        ': '}
                    </span>
                    <span style={{ marginRight: '10px' }}>
                      {convertToRomanNumerals(
                        journey.master_course_course.index + 1
                      ) +
                        '. ' +
                        journey.name}
                    </span>
                    <span>{levelData.name}</span>
                  </Caption6>
                  <Title>{activity.name}</Title>
                </div>
              </FlexContainer>
              {!isEditing ? (
                activity.in_draft ? (
                  <div
                    data-for="publishActivityTooltip"
                    data-tip="Please enter all required fields prior to publishing this activity."
                  >
                    <Button
                      width="184px"
                      title="Publish Activity"
                      buttonColor={colors.oeYellow}
                      borderColor={colors.oeYellow}
                      hoverBackground={colors.charcoal4}
                      hoverColor={colors.white}
                      shouldShowTooltip={true}
                      shouldShowTooltipAndDisableButton={!isActivityPublishable}
                      handleClick={
                        isActivityPublishable
                          ? () => {
                              dispatch(
                                showModal('PUBLISH_ACTIVITY_MODAL', {
                                  levelId,
                                  activityQuestId,
                                  onPublish: handleCloseEditMode
                                })
                              )
                            }
                          : null
                      }
                    />
                    {!isActivityPublishable && (
                      <ReactTooltip
                        id="publishActivityTooltip"
                        place="bottom"
                        effect="solid"
                        className="activityCourseTooltip"
                      />
                    )}
                  </div>
                ) : (
                  <FlexContainer
                    style={{
                      flexDirection: 'column',
                      alignItems: 'flex-end'
                    }}
                  >
                    <H3 style={{ color: colors.jade }}>Published</H3>
                    <Caption1>
                      {moment
                        .unix(activity.last_published_at)
                        .format('DD MMMM YYYY')
                        .toString()}
                    </Caption1>
                  </FlexContainer>
                )
              ) : null}
            </HeaderContainer>
            <ActivityContainer isEditing={isEditing}>
              {!isEditing ? (
                <ReadOnlyActivityData
                  activity={activity}
                  mentorFiles={mentorFiles}
                  resources={arraySort(resources, 'sort_order')}
                  linkedSkills={linkedSkills}
                  handleClick={handleEditClick}
                  isDraft={activity.in_draft}
                />
              ) : (
                <EditActivityDetails
                  activity={activity}
                  mentorFiles={mentorFiles}
                  resources={resources}
                  linkedSkills={linkedSkills}
                  activityName={activity.name}
                  activityId={activityQuestId}
                  levelId={levelId}
                  handleCloseEditMode={handleCloseEditMode}
                  isEditLink={isEditLink}
                />
              )}
            </ActivityContainer>
          </PageContainer>
        </React.Fragment>
      )}
    </div>
  )
}

ActivityDetails.propTypes = {
  apiData: PropTypes.object.isRequired,
  courseId: PropTypes.string.isRequired,
  journeyId: PropTypes.string.isRequired,
  levelId: PropTypes.string.isRequired,
  activityQuestId: PropTypes.string.isRequired
}

export const mapStateToProps = (state, ownProps) => ({
  courseId: ownProps.params.courseId,
  journeyId: ownProps.params.journeyId,
  levelId: ownProps.params.levelId,
  activityQuestId: ownProps.params.activityQuestId
})

export default withApiData(connect(mapStateToProps)(ActivityDetails))



// WEBPACK FOOTER //
// ./src/components/mycourses/Activities/ActivityDetails.js